import { useState } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, CheckCircle2, Bell, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

/**
 * Mobile "Confirm Receipt" banner for the worker's Job Detail screen.
 *
 * Renders one of three states based on the allocation status:
 *   sent              → yellow banner + Confirm Receipt button (+ notes)
 *   sent + re-ack     → amber banner "Allocation updated — please re-confirm"
 *   confirmed         → green pill "Confirmed · {relative time}"
 *
 * Does not render if there's no allocation, or if the flag-gated status fields
 * aren't present (auto-safe: worker just sees the job as before).
 */
export const MobileJobConfirmBanner = ({ allocation, onChange }) => {
  const { api } = useMobileAuth();
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  if (!allocation?.id) return null;

  const status = allocation.status;
  const requiresReAck = !!allocation.requires_re_ack;
  const ackAt = allocation.worker_ack_at;
  const sentAt = allocation.last_sent_at;
  const isWorkerAlloc = !!allocation.is_worker_allocation;

  // Hide entirely if the allocation isn't in the new workflow (e.g. flag off, legacy data).
  if (status !== 'sent' && status !== 'confirmed') return null;

  const endpoint = isWorkerAlloc
    ? `/api/mobile/worker-allocations/${allocation.id}/acknowledge`
    : `/api/mobile/allocations/${allocation.id}/acknowledge`;

  const doAcknowledge = async () => {
    setBusy(true);
    try {
      await api.post(endpoint, { notes: notes.trim() || undefined });
      toast.success('Thanks — confirmed!');
      setNotes('');
      setShowNotes(false);
      onChange?.();
    } catch (err) {
      toast.error('Could not confirm', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  // ==================== STATE: confirmed (no re-ack) ====================
  if (status === 'confirmed' && !requiresReAck) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm"
        data-testid="mobile-ack-confirmed"
      >
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        <span className="text-emerald-300">
          ✓ Confirmed
          {ackAt ? ` · ${formatDistanceToNow(new Date(ackAt), { addSuffix: true })}` : ''}
        </span>
      </div>
    );
  }

  // ==================== STATE: sent w/ re-ack required ====================
  if (requiresReAck) {
    return (
      <div
        className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
        data-testid="mobile-ack-reack"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-200">Allocation updated</div>
            <div className="text-amber-300/90 mt-0.5">
              Details on your job have changed. Please re-confirm receipt.
            </div>
          </div>
        </div>
        {showNotes && (
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note (e.g. running 10 min late)"
            rows={2}
            className="text-sm"
            data-testid="mobile-ack-notes-input"
          />
        )}
        <div className="flex gap-2">
          <Button
            onClick={doAcknowledge}
            disabled={busy}
            className="flex-1 bg-amber-500 text-black hover:bg-amber-400"
            data-testid="mobile-reack-btn"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Re-confirm Receipt
          </Button>
          {!showNotes && (
            <Button
              variant="outline"
              onClick={() => setShowNotes(true)}
              disabled={busy}
              className="text-xs"
            >
              Add note
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ==================== STATE: sent ====================
  return (
    <div
      className="space-y-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3"
      data-testid="mobile-ack-sent"
    >
      <div className="flex items-start gap-2">
        <Bell className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-yellow-200">New job — please confirm</div>
          <div className="text-yellow-300/90 mt-0.5">
            Tap Confirm Receipt to let dispatch know you've got the details.
            {sentAt && (
              <span className="opacity-70"> · Sent {formatDistanceToNow(new Date(sentAt), { addSuffix: true })}</span>
            )}
          </div>
        </div>
      </div>
      {showNotes && (
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional note"
          rows={2}
          className="text-sm"
          data-testid="mobile-ack-notes-input"
        />
      )}
      <div className="flex gap-2">
        <Button
          onClick={doAcknowledge}
          disabled={busy}
          className="flex-1 bg-yellow-400 text-black hover:bg-yellow-300"
          data-testid="mobile-ack-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          Confirm Receipt
        </Button>
        {!showNotes && (
          <Button
            variant="outline"
            onClick={() => setShowNotes(true)}
            disabled={busy}
            className="text-xs"
          >
            Add note
          </Button>
        )}
      </div>
    </div>
  );
};

export default MobileJobConfirmBanner;
